import { StrictMode, Component, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { ClerkProvider, type ClerkProviderProps } from '@clerk/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useClerkAppearance } from './hooks/useClerkAppearance'
import App from './App'
import './index.css'

// React Query defaults tuned for this app:
//   - staleTime: 5 min so most reads dedupe across mounts; per-query overrides
//     bump it higher for slow-changing data (daily astro = 1 hr).
//   - gcTime: 30 min so cached data survives route changes within a session.
//   - refetchOnWindowFocus: false — our data is mostly time-of-day-driven, not
//     user-action-driven, so refocus-refetch is just noise.
//   - retry: 1 — one quick retry on transient network errors, then surface
//     the failure to the UI.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 20, color: 'var(--color-text-primary)', background: 'var(--color-cosmic-black)', minHeight: '100vh', fontFamily: 'monospace' }}>
          <h1 style={{ color: 'var(--color-rose)' }}>Something went wrong</h1>
          {/* theme-audit: unmapped #e8a99e — error-message tint, no palette token */}
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, marginTop: 10, color: '#e8a99e' }}>
            {this.state.error.message}
          </pre>
          {/* theme-audit: unmapped #6e5f80 — stack-trace muted tint, no palette token */}
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 10, marginTop: 10, color: '#6e5f80' }}>
            {this.state.error.stack}
          </pre>
          {/* theme-audit: unmapped #c9a84c — legacy amber reload button, no palette token */}
          <button onClick={() => { this.setState({ error: null }); window.location.href = '/' }}
            style={{ marginTop: 20, padding: '10px 20px', background: '#c9a84c', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
            Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

function ClerkProviderWithAppearance({ children, ...props }: Omit<ClerkProviderProps, 'children'> & { children: ReactNode }) {
  const appearance = useClerkAppearance()
  return <ClerkProvider {...props} appearance={appearance}>{children}</ClerkProvider>
}

const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {CLERK_KEY ? (
          <ClerkProviderWithAppearance publishableKey={CLERK_KEY}>
            <App />
          </ClerkProviderWithAppearance>
        ) : (
          <App />
        )}
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
)
